import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';

export default function MessagesPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  const isGuest = user && (!user.name || user.name === 'Guest User');

  useEffect(() => {
    if (!user || !user.phone || isGuest) return;
    const myIndexPhone = user.phone.replace(/[.#$[\]]/g, '');
    const userChatsRef = ref(db, 'userChats/' + myIndexPhone);
    const unsubscribe = onValue(userChatsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.values(data).sort((a, b) => (b.lastTimestamp || 0) - (a.lastTimestamp || 0));
        setConversations(list);
      } else {
        setConversations([]);
      }
    });
    return () => unsubscribe();
  }, [user]);

  const openChat = (convo) => {
    navigate(
      '/chat?chatWithName=' + encodeURIComponent(convo.otherName) +
      '&chatWithPhone=' + encodeURIComponent(convo.otherPhone) +
      '&from=' + encodeURIComponent(convo.from) +
      '&to=' + encodeURIComponent(convo.to)
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const msgDate = new Date(timestamp);
    if (now.toDateString() === msgDate.toDateString()) {
      return msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return msgDate.toLocaleDateString([], { day: '2-digit', month: 'short' });
  };

  if (!user) return null;

  if (isGuest) {
    return (
      <div style={styles.page}>
        <Navbar userName={user.name} userPhone={user.phone} />
        <div style={styles.emptyWrap}>
          <div style={{ fontSize: 50, marginBottom: 14 }}>🔐</div>
          <h2 style={{ color: '#1a1a2e' }}>Login required</h2>
          <p style={{ color: '#9499b5' }}>Please login to see your messages</p>
          <button style={styles.loginBtn} onClick={() => navigate('/')}>Login / Sign Up</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <h2 style={styles.pageTitle}>💬 Messages</h2>

        {conversations.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>💬</div>
            <h3 style={{ color: '#1a1a2e', margin: '0 0 4px' }}>No conversations yet</h3>
            <p style={{ color: '#9499b5', margin: 0 }}>Chat with a driver or passenger to see it here</p>
          </div>
        ) : (
          <div style={styles.list}>
            {conversations.map(item => {
              const isUnread = item.lastSenderPhone !== user.phone;
              return (
                <div key={item.chatId} style={styles.convoRow} onClick={() => openChat(item)}>
                  <div style={styles.avatar}>
                    {(item.otherName || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.topRow}>
                      <span style={styles.name}>{item.otherName}</span>
                      <span style={styles.time}>{formatTime(item.lastTimestamp)}</span>
                    </div>
                    <div style={styles.route}>{item.from} → {item.to}</div>
                    <div style={{ ...styles.lastMsg, ...(isUnread ? styles.lastMsgUnread : {}) }}>{item.lastMessage}</div>
                  </div>
                  {isUnread && <div style={styles.unreadDot}></div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 700, margin: '0 auto', padding: '30px 20px 40px' },
  pageTitle: { color: '#1a1a2e', marginBottom: 20 },
  emptyState: { background: '#f7f9fc', borderRadius: 14, padding: 40, textAlign: 'center' },
  emptyWrap: { textAlign: 'center', padding: '100px 20px' },
  loginBtn: { background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', marginTop: 16 },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  convoRow: {
    display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #eef1f8',
    borderRadius: 12, padding: 12, cursor: 'pointer', position: 'relative',
  },
  avatar: {
    width: 44, height: 44, borderRadius: 22, background: '#3a8fd0', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0,
  },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: 14, fontWeight: 700, color: '#1a1a2e' },
  time: { fontSize: 11, color: '#9499b5' },
  route: { fontSize: 11, color: '#5a5f7d', marginTop: 1 },
  lastMsg: { fontSize: 12, color: '#9499b5', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  lastMsgUnread: { color: '#1a1a2e', fontWeight: 600 },
  unreadDot: { width: 8, height: 8, borderRadius: 4, background: '#e94560', flexShrink: 0 },
};